import type { JSX } from 'react';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Form, Input } from 'antd';
import { LockOutlined } from '@ant-design/icons';

import { authApi } from '../lib/authApi';
import { HubLogo } from '../components/HubLogo';

/**
 * Принудительная смена пароля при первом входе сотрудника (когда
 * EmployeeAccount.mustChangePassword=true). Backend форсит это на уровне
 * сессии — даже если юзер обойдёт фронт-роутер, любые API кроме
 * change-password будут возвращать 403 (TODO в Auth.4).
 *
 * Минимальные требования к паролю: 8 символов. Подсказку «не используй
 * `123456`» вынес во вторую строку.
 */
export function ChangePasswordPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: ({
      currentPassword,
      newPassword,
    }: {
      currentPassword: string;
      newPassword: string;
    }) => authApi.changePassword(currentPassword, newPassword),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wiki-auth', 'me'] });
      navigate('/', { replace: true });
    },
    onError: (e: Error) => setErrorMsg(e.message),
  });

  const pwdRules = [
    { required: true, message: 'Придумайте пароль' },
    { min: 8, message: 'Минимум 8 символов' },
  ];
  return (
    <div className="fk-hub-auth">
      <div className="fk-hub-auth-card" style={{ maxWidth: 440 }}>
        <div className="fk-hub-auth-head">
          <HubLogo size="lg" />
          <div>
            <h1>Задайте свой пароль</h1>
            <p>Это разово. Пароль от управляющего больше не понадобится.</p>
          </div>
        </div>

        {errorMsg && (
          <Alert type="error" message={errorMsg} showIcon style={{ marginBottom: 16 }} closable onClose={() => setErrorMsg(null)} />
        )}

        <Form
          layout="vertical"
          requiredMark={false}
          onFinish={(v) => mutation.mutate({ currentPassword: v.currentPassword, newPassword: v.newPassword })}
        >
          <Form.Item label="Старый пароль (от управляющего)" name="currentPassword" rules={[{ required: true, message: 'Введите старый пароль' }]}>
            <Input.Password size="large" prefix={<LockOutlined style={{ color: 'var(--fk-text-muted)' }} />} autoComplete="current-password" autoFocus />
          </Form.Item>
          <Form.Item label="Новый пароль (минимум 8 символов)" name="newPassword" rules={pwdRules} hasFeedback>
            <Input.Password size="large" prefix={<LockOutlined style={{ color: 'var(--fk-text-muted)' }} />} autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            label="Повторите новый пароль"
            name="confirmPassword"
            dependencies={['newPassword']}
            hasFeedback
            rules={[
              { required: true, message: 'Повторите пароль' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                  return Promise.reject(new Error('Пароли не совпадают'));
                },
              }),
            ]}
          >
            <Input.Password size="large" prefix={<LockOutlined style={{ color: 'var(--fk-text-muted)' }} />} autoComplete="new-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" size="large" block loading={mutation.isPending} style={{ fontWeight: 700, height: 48 }}>
            Сохранить и войти
          </Button>
        </Form>
      </div>
    </div>
  );
}
